import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import {
  Firestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  addDoc,
} from '@angular/fire/firestore';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { MatDialog } from '@angular/material/dialog';
import { UploadLpoDialogComponent } from './upload-lpo-dialog/upload-lpo-dialog.component';
import { ReviewDialogComponent } from '../review-dialog/review-dialog.component';
import { ServiceOfferDialogComponent } from '../service-offer-dialog/service-offer-dialog.component';
import { BookingAutoRejectService } from 'src/app/shared/services/booking-auto-reject.service';
import { LpoViewerDialogComponent } from 'src/app/components/lpo-viewer-dialog/lpo-viewer-dialog.component';
import { TimesheetEntryDialogComponent } from './timesheet-entry-dialog/timesheet-entry-dialog.component';
import { InvoiceTemplateDialogComponent } from 'src/app/components/invoice-template-dialog/invoice-template-dialog.component';
import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/shared/services/notification.service';

@Component({
  selector: 'app-booking-orders',
  templateUrl: './booking-orders.component.html',
  styleUrls: ['./booking-orders.component.scss'],
})
export class BookingOrdersComponent implements OnInit {
  bookings: any[] = [];
  groupedBookings: any[] = []; // ✅ Added for grouping UI
  isLoading = true;
  errorMessage = '';

  expandedBookingId: string | null = null;
  vendorDetailsMap: Record<string, any> = {};
  private rtSubscriptions: Record<string, Subscription> = {};

  // 🔍 Search & Filtering
  searchTerm = '';
  selectedStatus = 'All';
  selectedEquipment = 'All';
  filteredBookings: any[] = [];
  availableStatuses = ['All', 'Pending', 'Accepted', 'Confirmed', 'Started', 'Completed', 'Unavailable', 'Cancelled'];
  availableEquipmentTypes: string[] = ['All'];

  // 📄 Pagination Properties
  pageSize = 10;
  pageIndex = 0;
  paginatedBookings: any[] = [];

  constructor(
    private afs: Firestore,
    private auth: Auth,
    private router: Router,
    private ux: UxserviceService,
    private dialog: MatDialog,
    private autoReject: BookingAutoRejectService,
    private rtBooking: RealtimeBookingService,
    private location: Location,
    private notificationService: NotificationService
  ) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  openServiceOfferDialog() {
    this.router.navigate(['/register-business']);
  }

  ngOnDestroy() {
    Object.values(this.rtSubscriptions).forEach((sub) => sub.unsubscribe());
  }

  ngOnInit() {
    onAuthStateChanged(this.auth, async (user: User | null) => {
      if (user) {
        // Restore filters from sessionStorage
        this.restoreFilters();

        // Run auto-reject check in background so it doesn't block UI load
        this.autoReject
          .checkAndRejectBookings()
          .catch((err) => console.error('Auto-reject failed', err));
        // Track unread notifications for each booking to show/hide bell dot
        const notesSub = this.notificationService.notifications$.subscribe(notes => {
          this.bookings.forEach(b => {
             // Matches either direct bookingId field OR the document ID being present in the notification link
             b.hasUpdates = notes.some(n => 
               !n.isRead && (n.bookingId === b.id || (n.link && n.link.includes(b.id)))
             );
          });
          this.updatePaginatedBookings(); // Refresh current page view
        });
        this.rtSubscriptions['notifications'] = notesSub;

        await this.loadBookingsWithOverlay(user.uid);
      } else {
        this.errorMessage = 'Please log in to view your bookings.';
        this.isLoading = false;
      }
    });
  }



  /** Fetch bookings with loading overlay */
  async loadBookingsWithOverlay(userId: string) {
    this.isLoading = true;
    await this.ux.showOverlay('Loading your bookings...');
    try {
      await this.fetchBookings(userId);
    } catch (err) {
      console.error('Error loading bookings:', err);
      this.errorMessage = 'Failed to load bookings.';
    } finally {
      this.isLoading = false;
      this.ux.instantCloseOverlay();
    }
  }



  /** Load booking list */
  async fetchBookings(userId: string) {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const bookingsRef = collection(this.afs, 'booking-list');
      const q = query(bookingsRef, where('bywhom', '==', userId));
      const snapshot = await getDocs(q);

      const bookingPromises = snapshot.docs.map(async (docSnap) => {
        const data: any = docSnap.data();

        // Handle both new flat structure and old nested structure
        const start =
          data.startDate?.toDate?.() ||
          data.customer?.startDate?.toDate?.() ||
          null;
        const end =
          data.endDate?.toDate?.() ||
          data.customer?.endDate?.toDate?.() ||
          null;

        let months = 0;
        let weeks = 0;

        if (start && end) {
          const diffMs = end.getTime() - start.getTime();
          const days = diffMs / (1000 * 3600 * 24);

          months = Math.ceil(days / 30);
          weeks = Math.ceil(days / 7);
        }

        let equipment = data.equipment;
        if (data.equipmentId) {
          try {
            const eqRef = doc(this.afs, 'equipment-list', data.equipmentId);
            const eqSnap = await getDoc(eqRef);
            if (eqSnap.exists()) {
              equipment = {
                ...equipment,
                ...eqSnap.data()
              };
            }
          } catch (e) {
            console.error(
              'Error fetching equipment for booking',
              docSnap.id,
              e,
            );
          }
        }

        // Polyfill customer object for template compatibility
        const polyCustomer = data.customer || {};
        if (data.startDate) polyCustomer.startDate = data.startDate;
        if (data.endDate) polyCustomer.endDate = data.endDate;

        const booking = {
          id: docSnap.id,
          ...data,
          customer: polyCustomer, // Ensure customer object with dates exists
          equipment, // Ensure equipment is available
          computedMonths: months,
          computedWeeks: weeks,
          reviewExists: false,
          reviewId: null,
        };

        this.initRealtimeBooking(booking);
        return booking;
      });

      this.bookings = await Promise.all(bookingPromises);

      // Extract unique equipment types/names for filtering
      this.extractEquipmentTypes();

      // 🔥 Pre-fetch vendor details for all bookings to enable searching by vendor name
      await this.prefetchAllVendorDetails();

      // Sort newest first
      this.bookings.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return bDate.getTime() - aDate.getTime();
      });

      // 🔥 Group bookings by ID
      this.groupBookings();

      // 🔥 Load review info for each booking concurrently
      await Promise.all(
        this.bookings.map((booking) => this.loadUserReview(booking)),
      );

      // 🔥 Initialize filtered list (Don't reset pageIndex if restoring)
      this.applyFilter(false);

      if (this.bookings.length === 0) {
        this.errorMessage = 'No bookings found.';
      }
    } catch (err) {
      console.error('❌ Error fetching bookings:', err);
      this.errorMessage = 'Failed to load bookings.';
    } finally {
      this.isLoading = false;
    }
  }

  /** Update the slice of bookings shown on current page */
  updatePaginatedBookings() {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedBookings = this.filteredBookings.slice(startIndex, endIndex);

    // Save pagination state
    this.saveFilters();
  }

  /** Apply Search Filter */
  applyFilter(isUserAction: boolean = true) {
    const term = this.searchTerm.toLowerCase().trim();
    const status = this.selectedStatus;
    const equipmentType = this.selectedEquipment;

    this.filteredBookings = this.bookings.filter((b) => {
      // Status matching
      const effectiveFilterStatus = status === 'Unavailable' ? 'rejected' : status.toLowerCase();
      const bStatus = (b.status || 'Pending').toLowerCase();
      const matchesStatus = status === 'All' || bStatus === effectiveFilterStatus;
      if (!matchesStatus) return false;

      // Equipment matching
      const bEqName = (b.equipment?.title || b.equipment?.name || 'Unknown');
      const matchesEquipment = equipmentType === 'All' || bEqName === equipmentType;
      if (!matchesEquipment) return false;

      // Term matching
      if (!term) return true;

      const vehicleName = (b.equipment?.title || b.equipment?.name || '').toLowerCase();
      const brand = (b.equipment?.brand || '').toLowerCase();
      const model = (b.equipment?.model || '').toLowerCase();
      const bookingId = (b.bookingId || '').toLowerCase();
      
      // Vendor info from map
      const vendor = this.vendorDetailsMap[b.id] || {};
      const vendorName = (vendor.companyname || '').toLowerCase();

      // Dates
      const startDate = b.customer?.startDate?.toDate ? b.customer.startDate.toDate().toLocaleDateString() : '';
      const endDate = b.customer?.endDate?.toDate ? b.customer.endDate.toDate().toLocaleDateString() : '';
      const createdAt = b.createdAt?.toDate ? b.createdAt.toDate().toLocaleDateString() : '';

      return (
        vehicleName.includes(term) ||
        brand.includes(term) ||
        model.includes(term) ||
        bookingId.includes(term) ||
        vendorName.includes(term) ||
        startDate.toLowerCase().includes(term) ||
        endDate.toLowerCase().includes(term) ||
        createdAt.toLowerCase().includes(term)
      );
    });

    if (isUserAction) {
      this.pageIndex = 0; // Reset to first page on search
    }
    this.updatePaginatedBookings();
    this.saveFilters();
  }

  /** Extract unique equipment types from bookings */
  extractEquipmentTypes() {
    const types = new Set<string>();
    this.bookings.forEach((b) => {
      const name = b.equipment?.title || b.equipment?.name;
      if (name) types.add(name);
    });
    this.availableEquipmentTypes = ['All', ...Array.from(types).sort()];
  }

  /** Reset all filters */
  resetFilters() {
    this.searchTerm = '';
    this.selectedStatus = 'All';
    this.selectedEquipment = 'All';
    this.pageIndex = 0;
    sessionStorage.removeItem('booking_filters_customer');
    this.applyFilter();
  }

  /** Save filters to sessionStorage */
  private saveFilters() {
    const state = {
      searchTerm: this.searchTerm,
      selectedStatus: this.selectedStatus,
      selectedEquipment: this.selectedEquipment,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize
    };
    sessionStorage.setItem('booking_filters_customer', JSON.stringify(state));
  }

  /** Restore filters from sessionStorage */
  private restoreFilters() {
    const saved = sessionStorage.getItem('booking_filters_customer');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        this.searchTerm = state.searchTerm || '';
        this.selectedStatus = state.selectedStatus || 'All';
        this.selectedEquipment = state.selectedEquipment || 'All';
        this.pageIndex = state.pageIndex || 0;
        this.pageSize = state.pageSize || 10;
      } catch (e) {
        console.error('Error restoring filters', e);
      }
    }
  }

  /** Pre-fetch vendor details for all fetched bookings */
  async prefetchAllVendorDetails() {
    const vendorIds = [...new Set(this.bookings.map(b => b.equipment?.bywhom).filter(id => !!id))];
    
    // We'll cache vendor data by vendorId first to avoid duplicate requests, 
    // then map it back to booking IDs as expected by the existing UI logic.
    const vendorCache: Record<string, any> = {};

    await Promise.all(vendorIds.map(async (vId) => {
      try {
        const vendorRef = doc(this.afs, 'users-list', vId);
        const vendorSnap = await getDoc(vendorRef);
        if (vendorSnap.exists()) {
          const vendorData = vendorSnap.data();
          const account = vendorData['accountdetails'] || {};
          vendorCache[vId] = {
            companyname: account['companyname'] || vendorData['displayName'] || 'N/A',
            email: account['emailid'] || account['email'] || vendorData['email'] || 'N/A',
            phone: account['phoneNumber'] || account['phone'] || vendorData['phoneNumber'] || vendorData['phone'] || 'N/A',
          };
        }
      } catch (e) {
        console.error('Error pre-fetching vendor', vId, e);
      }
    }));

    // Map back to booking IDs
    this.bookings.forEach(b => {
      const vId = b.equipment?.bywhom;
      if (vId && vendorCache[vId]) {
        this.vendorDetailsMap[b.id] = vendorCache[vId];
      }
    });
  }

  /** Handle Page Event */
  onPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedBookings();
    
    // Scroll to top of table container for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Group bookings by Booking ID */
  groupBookings() {
    const groups: Record<string, any> = {};

    this.bookings.forEach((b) => {
      const bId = b.bookingId || 'UNKNOWN';
      if (!groups[bId]) {
        groups[bId] = {
          bookingId: bId,
          createdAt: b.createdAt, // Take date from first item
          totalAmount: 0,
          items: [],
        };
      }
      groups[bId].items.push(b);
      groups[bId].totalAmount += b.totalAmount || 0;
    });

    // Convert to array and sort by date desc
    this.groupedBookings = Object.values(groups).sort((a: any, b: any) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
      return bDate.getTime() - aDate.getTime();
    });
  }

  initRealtimeBooking(booking: any) {
    if (this.rtSubscriptions[booking.id]) return;

    let initialLoad = true;

    this.rtSubscriptions[booking.id] = this.rtBooking
      .watchBooking(booking.id)
      .subscribe((rtData) => {
        if (!rtData) return;

        // Check if anything actually changed to trigger the bell
        let changed = false;

        // 1. Root-level status and timestamp check
        if (rtData.updatedAt && rtData.updatedAt !== booking.lastRtdbUpdate) {
          booking.lastRtdbUpdate = rtData.updatedAt;
          changed = true;
        }
        if (rtData.status && rtData.status !== booking.status) {
          booking.status = rtData.status;
          changed = true;
        }

        // 2. LPO Section (Approved, Rejected, Generated, URL)
        if (rtData.lpo) {
          const oldLpoStr = JSON.stringify(booking.lpo || {});
          const newLpoStr = JSON.stringify(rtData.lpo);
          
          if (
            newLpoStr !== oldLpoStr ||
            rtData.lpo.approved !== booking.lpoApproved ||
            rtData.lpo.rejected !== booking.lpoRejected
          ) {
            booking.lpo = rtData.lpo;
            booking.lpoApproved = rtData.lpo.approved;
            booking.lpoRejected = rtData.lpo.rejected;
            booking.lpoGenerated = rtData.lpo.generated;
            booking.LPO = rtData.lpo.url;
            booking.lpoDate = rtData.lpo.date;
            if (rtData.lpo.totalAmount !== undefined) {
              booking.totalAmount = rtData.lpo.totalAmount;
            }
            if (rtData.lpo.awaitingCustomerPriceConsent !== undefined) {
              booking.awaitingCustomerPriceConsent = rtData.lpo.awaitingCustomerPriceConsent;
            }
            changed = true;
          }
        }

        // 3. Timesheet Section (Rows, Signs, Completion, Steps)
        if (rtData.timesheet) {
          const oldTsStr = JSON.stringify(booking.timesheet || {});
          const newTsStr = JSON.stringify(rtData.timesheet);
          if (oldTsStr !== newTsStr) {
            booking.timesheet = rtData.timesheet;
            changed = true;
          }
        }

        // 4. Invoice Section (Finalization, Amounts, Numbers, Files)
        if (rtData.invoice) {
          const oldInvStr = JSON.stringify(booking.invoice || {});
          const newInvStr = JSON.stringify(rtData.invoice);
          if (oldInvStr !== newInvStr) {
            booking.invoice = rtData.invoice;
            if (rtData.invoice.finalized !== undefined) {
              booking.invoiceFinalized = rtData.invoice.finalized;
            }
            changed = true;
          }
        }

        // 5. File Uploads (General)
        if (rtData.uploadedTimesheetUrl && rtData.uploadedTimesheetUrl !== booking.uploadedTimesheetUrl) {
          booking.uploadedTimesheetUrl = rtData.uploadedTimesheetUrl;
          changed = true;
        }
        if (rtData.uploadedInvoiceUrl && rtData.uploadedInvoiceUrl !== booking.uploadedInvoiceUrl) {
          booking.uploadedInvoiceUrl = rtData.uploadedInvoiceUrl;
          changed = true;
        }

        // Don't trigger bell on the very first load
        if (changed && !initialLoad) {
          booking.hasUpdates = true;
        }

        initialLoad = false;
      });
  }

  /** Load existing review for user+booking */
  async loadUserReview(booking: any) {
    const equipmentId = booking.equipmentId || booking.equipment?.id;
    const userId = this.auth.currentUser?.uid;

    if (!equipmentId || !userId) return;

    const reviewsRef = collection(this.afs, `reviews/${equipmentId}/items`);

    const q = query(
      reviewsRef,
      where('userId', '==', userId),
      where('bookingId', '==', booking.id),
    );

    const snap = await getDocs(q);

    if (!snap.empty) {
      const reviewDoc = snap.docs[0];
      const review = reviewDoc.data();

      booking.reviewExists = true;
      booking.reviewId = reviewDoc.id;
      booking.userRating = review['rating'];

      booking.fullReview = {
        rating: review['rating'],
        finalReview: review['finalReview'],
        questions: review['questions'],
      };
    }
  }

  /** View vendor details */
  async viewBooking(b: any) {
    if (this.expandedBookingId === b.id) {
      this.expandedBookingId = null;
      return;
    }

    if (this.vendorDetailsMap[b.id]) {
      this.expandedBookingId = b.id;
      return;
    }

    await this.ux.showOverlay('Fetching vendor details...');
    try {
      const vendorId = b.equipment?.bywhom;
      if (!vendorId) {
        this.ux.showError('Vendor ID missing!');
        return;
      }

      const vendorRef = doc(this.afs, 'users-list', vendorId);
      const vendorSnap = await getDoc(vendorRef);

      if (vendorSnap.exists()) {
        const vendorData = vendorSnap.data();
        const account = vendorData['accountdetails'] || {};

        this.vendorDetailsMap[b.id] = {
          companyname:
            account['companyname'] || vendorData['displayName'] || 'N/A',
          email:
            account['emailid'] ||
            account['email'] ||
            vendorData['email'] ||
            'N/A',
          phone:
            account['phoneNumber'] ||
            account['phone'] ||
            vendorData['phoneNumber'] ||
            vendorData['phone'] ||
            'N/A',
        };

        this.expandedBookingId = b.id;
      }
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  /** Upload LPO */
  openLpoDialog(booking: any) {
    const dialogRef = this.dialog.open(UploadLpoDialogComponent, {
      width: '800px',
      data: { bookingId: booking.id, lpoUrl: booking.LPO || null },
      panelClass: 'modern-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.uploadedUrl) {
        booking.LPO = result.uploadedUrl;
        this.ux.showSuccess('LPO uploaded!');
      }
    });
  }

  /** Open Review Dialog (Write or Edit) */
  openReviewDialog(booking: any) {
    const dialogRef = this.dialog.open(ReviewDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {
        equipmentId: booking.equipmentId || booking.equipment?.id,
        existingReview: booking.reviewExists ? booking.fullReview : null,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) return;

      const equipId = booking.equipmentId || booking.equipment?.id;
      const vendorId = booking.equipment?.bywhom;
      const userId = this.auth.currentUser?.uid;

      if (!equipId || !userId || !vendorId) return;

      try {
        await this.ux.showOverlay('Submitting review...');

        // 1. SAVE EQUIPMENT REVIEW
        const equipReviewData = {
          ...result.equipment,
          userId,
          bookingId: booking.id,
          updatedAt: new Date()
        };

        if (booking.reviewExists && booking.reviewId) {
          const equipRevDoc = doc(this.afs, `reviews/${equipId}/items/${booking.reviewId}`);
          await updateDoc(equipRevDoc, equipReviewData);
        } else {
          const equipRevColl = collection(this.afs, `reviews/${equipId}/items`);
          const newDoc = await addDoc(equipRevColl, { ...equipReviewData, createdAt: new Date() });
          booking.reviewId = newDoc.id;
          booking.reviewExists = true;
        }

        // 2. SAVE VENDOR REVIEW (Separate Collection)
        const vendorReviewData = {
          ...result.vendor,
          userId,
          bookingId: booking.id,
          equipmentId: equipId,
          updatedAt: new Date()
        };
        
        const vendorRevColl = collection(this.afs, `vendor-reviews/${vendorId}/items`);
        const vQuery = query(vendorRevColl, where('bookingId', '==', booking.id));
        const vSnap = await getDocs(vQuery);

        if (!vSnap.empty) {
          await updateDoc(doc(this.afs, `vendor-reviews/${vendorId}/items/${vSnap.docs[0].id}`), vendorReviewData);
        } else {
          await addDoc(vendorRevColl, { ...vendorReviewData, createdAt: new Date() });
        }

        booking.fullReview = {
           equipment: result.equipment,
           vendor: result.vendor
        };
        booking.userRating = result.equipment.rating;

        this.ux.showSuccess('Review submitted successfully!');
      } catch (err) {
        console.error('Review submission error', err);
        this.ux.showError('Failed to submit review');
      } finally {
        this.ux.instantCloseOverlay();
      }
    });
  }

  /** Generate LPO */
  generateLpo(booking: any) {
    if (this.dialog.getDialogById('lpo-viewer-dialog')) return;
    const dialogRef = this.dialog.open(LpoViewerDialogComponent, {
      id: 'lpo-viewer-dialog',
      width: '900px',
      maxHeight: '90vh',
      data: { booking },
      panelClass: 'lpo-dialog',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.saved) {
        this.ux.showSuccess('LPO generated and sent to vendor!');
      }
    });
  }

  openTimesheetDialog(booking: any) {
    this.router.navigate(['/timesheet', booking.id]);
  }

  /** Open Invoice View Dialog */
  openInvoiceDialog(booking: any) {
    // If we have a direct URL, maybe we just open it?
    // But the dialog handles the template. If it's a finalizing logic,
    // the dialog should support view mode.
    this.dialog.open(InvoiceTemplateDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        bookingId: booking.id,
        booking: booking,
        isViewOnly: true, // Hint to dialog to just show it
      },
      panelClass: 'lpo-dialog',
    });
  }
}
